import { useMemo, useState } from 'react';

import type { PriorityTask } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  type DndContextProps,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

type UsePlaylistDndArgs = Readonly<{
  /** The array you are actually rendering right now (order matters). */
  visibleTasks: PriorityTask[];
  /** The scrollable container ref (drawer list). */
  listRef: React.RefObject<HTMLDivElement | null>;
  /** Playlist id for server update. */
  currentPlaylistId?: number | null;
  /** Promise-returning RTK action (already unwrapped). */
  updateTaskOrder: (args: { playlistId: number; taskIds: number[] }) => Promise<unknown>;
  /** Optimistic setter in the parent. */
  setLocalTasks: (tasks: PriorityTask[]) => void;
  /** Backup for rollback (usually last known good list). */
  sourceTasks: PriorityTask[];
  /** Optional autoscroll tuning. */
  autoscroll?: {
    edgeThresholdPx?: number;
    stepPx?: number;
  };
  /** Whether drag and drop is enabled. */
  isReorderMode: boolean;
}>;

type UsePlaylistDndReturn = Readonly<{
  sensors: DndContextProps['sensors'];
  itemIds: number[];
  /** The task currently held by the pointer (for DragOverlay). */
  activeTask: PriorityTask | null;
  /** Whether we are currently dragging. */
  dragging: boolean;
  onDragStart: (e: DragStartEvent) => void;
  onDragMove: (e: DragMoveEvent) => void;
  onDragEnd: (e: DragEndEvent) => Promise<void>;
}>;

export const usePlaylistDnd = ({
  visibleTasks,
  listRef,
  currentPlaylistId,
  updateTaskOrder,
  setLocalTasks,
  sourceTasks,
  autoscroll,
  isReorderMode,
}: UsePlaylistDndArgs): UsePlaylistDndReturn => {
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );

  const itemIds = useMemo(() => visibleTasks.map((t) => t.id), [visibleTasks]);

  const activeTask = useMemo(
    () => (activeTaskId ? (visibleTasks.find((t) => t.id === activeTaskId) ?? null) : null),
    [activeTaskId, visibleTasks],
  );

  const dragging = activeTaskId !== null;

  const edgeThresholdPx = autoscroll?.edgeThresholdPx ?? 96;
  const stepPx = autoscroll?.stepPx ?? 12;

  const onDragStart = ({ active }: DragStartEvent) => {
    if (!isReorderMode) return;

    setActiveTaskId(Number(active.id));

    // If we're currently in sorted mode, capture that order as our new manual order
    // This prevents visual jumping and makes the current sort the new "order"
    if (visibleTasks.length > 0) {
      // Always preserve the current visible order when starting to drag
      setLocalTasks([...visibleTasks]);
    }
  };

  const onDragMove = ({ delta }: DragMoveEvent) => {
    if (!isReorderMode) return;

    const el = listRef.current;

    if (!el) return;

    const rect = el.getBoundingClientRect();
    const pointerY = rect.top + (rect.height / 2 + delta.y);

    const nearTop = pointerY - rect.top < edgeThresholdPx;
    const nearBottom = rect.bottom - pointerY < edgeThresholdPx;

    if (nearTop) el.scrollTop -= stepPx;
    else if (nearBottom) el.scrollTop += stepPx;
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveTaskId(null);
    if (!isReorderMode || !over || active.id === over.id) return;

    const oldIndex = visibleTasks.findIndex((t) => t.id === Number(active.id));
    const newIndex = visibleTasks.findIndex((t) => t.id === Number(over.id));

    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(visibleTasks, oldIndex, newIndex);

    setLocalTasks(newOrder); // instant visual commit

    if (currentPlaylistId) {
      try {
        await updateTaskOrder({
          playlistId: currentPlaylistId,
          taskIds: newOrder.map((t) => t.id),
        });
      } catch (err) {
        // rollback
        setLocalTasks(sourceTasks);

        console.error('Failed to update task order:', err);
      }
    }
  };

  return {
    sensors,
    itemIds,
    activeTask,
    dragging,
    onDragStart,
    onDragMove,
    onDragEnd,
  };
};
